/**
 * Browser OCR engine factory: PP-OCRv6 via `ppu-paddle-ocr/web` and
 * onnxruntime-web, with all model + WASM assets served from this app
 * (no CDN dependency at runtime).
 */
import { OcrEngine, type OcrLogger } from './ocr';

export async function createBrowserOcrEngine(
  baseUrl: string,
  logger?: OcrLogger,
): Promise<OcrEngine> {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

  // Load ORT first and point it at our self-hosted WASM before the OCR
  // service module initializes its default (jsDelivr) path.
  const ort = await import('onnxruntime-web');
  ort.env.wasm.wasmPaths = `${base}ort/`;
  ort.env.wasm.numThreads = Math.max(
    1,
    Math.min(navigator.hardwareConcurrency || 4, 8),
  );

  const { PaddleOcrService } = await import('ppu-paddle-ocr/web');

  return new OcrEngine({
    logger,
    createService: async () =>
      new PaddleOcrService({
        model: {
          detection: `${base}models/PP-OCRv6_small_det.ort`,
          recognition: `${base}models/PP-OCRv6_small_rec.ort`,
          charactersDictionary: `${base}models/ppocrv6_dict.txt`,
        },
        processing: { engine: 'canvas-native' },
        // Prefer WebGPU when available (jsep WASM is served from R2); the
        // WASM backend remains the automatic fallback.
        session: { executionProviders: ['webgpu', 'wasm'], graphOptimizationLevel: 'all' },
        debugging: { verbose: false },
      }),
  });
}
